import type { DashboardCalendarCapacityRow } from "@/hooks/queries/calendar-capacities.queries";
import type { Calendar } from "@/types/appointment_calendar.types";

export type CalendarCapacityOption = {
    /** Stable key (calendar.name) — used for remote config and snapshot matching. */
    name: string;
    label: string;
    color?: string;
};

export enum CalendarCapacitiesSelectorVariant {
    LIST = "list",
    INLINE = "inline",
}

export type CalendarCapacitiesVisibleConfig = string[];

export function isCalendarCapacitySelected(selectedCalendars: string[], calendarName: string): boolean {
    return selectedCalendars.includes(calendarName);
}

export function filterVisibleCalendarRows(
    rows: DashboardCalendarCapacityRow[],
    selectedCalendars: CalendarCapacitiesVisibleConfig
): DashboardCalendarCapacityRow[] {
    if (selectedCalendars.length === 0) {
        return [];
    }
    const visible = new Set(selectedCalendars);
    return rows.filter((row) => visible.has(row.name));
}

export function isAllCalendarCapacitiesSelected(
    selectedCalendars: CalendarCapacitiesVisibleConfig,
    allCalendarNames: string[]
): boolean {
    if (allCalendarNames.length === 0) {
        return true;
    }
    return allCalendarNames.every((name) => selectedCalendars.includes(name));
}

export function toggleCalendarCapacitySelection(
    selectedCalendars: CalendarCapacitiesVisibleConfig,
    calendarName: string
): CalendarCapacitiesVisibleConfig {
    if (selectedCalendars.length === 0) {
        return [calendarName];
    }

    const next = selectedCalendars.includes(calendarName)
        ? selectedCalendars.filter((name) => name !== calendarName)
        : [...selectedCalendars, calendarName];

    return next;
}

export function toggleAllCalendarCapacitySelection(
    selectedCalendars: CalendarCapacitiesVisibleConfig,
    allCalendarNames: string[]
): CalendarCapacitiesVisibleConfig {
    if (isAllCalendarCapacitiesSelected(selectedCalendars, allCalendarNames)) {
        return [];
    }
    return [...allCalendarNames];
}

/** Options from calendar management when `GET /calendars` is available. */
export function calendarsToCapacityOptions(calendars: Calendar[]): CalendarCapacityOption[] {
    return calendars.map((calendar) => ({
        name: calendar.name,
        label: calendar.label,
        color: calendar.color,
    }));
}

/**
 * Selector options: snapshot rows are the source of truth; optional management calendars
 * merge in labels/colors when `GET /calendars` exists.
 */
export function buildCalendarCapacityOptions(
    calendars: Calendar[],
    snapshotRows: DashboardCalendarCapacityRow[]
): CalendarCapacityOption[] {
    const byName = new Map<string, CalendarCapacityOption>();

    for (const option of calendarsToCapacityOptions(calendars)) {
        byName.set(option.name, option);
    }

    for (const row of snapshotRows) {
        if (!byName.has(row.name)) {
            byName.set(row.name, {
                name: row.name,
                label: row.label,
                color: row.color,
            });
        }
    }

    return [...byName.values()].sort((a, b) => a.label.localeCompare(b.label));
}
